/**
 * Campground — procedural scatter of camping tents in Outeroo.
 *
 * Tents are simple triangular prisms with randomly tinted fabric. Tens of
 * thousands of these would melt the GPU, so we render them as InstancedMesh
 * with one prism geometry and color via instanceColor.
 */

import * as THREE from 'three';
import type { Terrain } from '@/world/Terrain';
import { mulberry32 } from '@/core/MathUtils';

const TENT_PALETTE = [
  '#cc4040',
  '#3a76c8',
  '#3a9a4a',
  '#d4a830',
  '#7a3aa8',
  '#cc6a30',
  '#888888',
  '#ddc8a8'
];

export class Campground {
  static build(
    terrain: Terrain,
    centers: { x: number; z: number; r: number; count: number }[],
    seed = 9001
  ): THREE.Group {
    const group = new THREE.Group();
    group.name = 'Campground';

    const totalCount = centers.reduce((s, c) => s + c.count, 0);
    if (totalCount === 0) return group;

    // Build a single triangular prism geometry for the tent shape.
    const tentGeo = new THREE.BufferGeometry();
    const positions = new Float32Array([
      // base front-left, base front-right, peak
      -1, 0, 1,  1, 0, 1,  0, 1.4, 0,
      // base back-left, base back-right, peak
      -1, 0, -1,  1, 0, -1,  0, 1.4, 0,
      // left fabric
      -1, 0, 1,  0, 1.4, 0,  -1, 0, -1,
      // right fabric
       1, 0, 1,  1, 0, -1,   0, 1.4, 0
    ]);
    tentGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    tentGeo.computeVertexNormals();

    const mat = new THREE.MeshStandardMaterial({
      vertexColors: true,
      roughness: 0.85,
      side: THREE.DoubleSide
    });
    const inst = new THREE.InstancedMesh(tentGeo, mat, totalCount);
    inst.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(totalCount * 3), 3);
    inst.castShadow = false;

    const m4 = new THREE.Matrix4();
    const color = new THREE.Color();
    const rand = mulberry32(seed);
    let i = 0;

    for (const c of centers) {
      let placed = 0;
      let attempts = 0;
      while (placed < c.count && attempts < c.count * 8) {
        attempts++;
        const a = rand() * Math.PI * 2;
        const r = Math.sqrt(rand()) * c.r;
        const x = c.x + Math.cos(a) * r;
        const z = c.z + Math.sin(a) * r;
        const y = terrain.heightAt(x, z);
        const slopeL = terrain.heightAt(x - 2, z);
        const slopeR = terrain.heightAt(x + 2, z);
        if (Math.abs(slopeL - slopeR) > 4) continue;
        const scale = 0.7 + rand() * 0.4;
        const rot = rand() * Math.PI * 2;
        m4.makeRotationY(rot);
        m4.scale(new THREE.Vector3(scale, scale, scale));
        m4.setPosition(x, y, z);
        inst.setMatrixAt(i, m4);
        const palette = TENT_PALETTE[Math.floor(rand() * TENT_PALETTE.length)];
        color.set(palette);
        inst.setColorAt(i, color);
        i++;
        placed++;
      }
    }
    inst.count = i;
    inst.instanceMatrix.needsUpdate = true;
    if (inst.instanceColor) inst.instanceColor.needsUpdate = true;
    group.add(inst);

    return group;
  }
}
